// Libs
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { getKanbanVersionWsValue } from '../../core/helper';
import { updateWorkspaceSetting } from '../../core/actions';

// KANBAN-V2 TOGGLE
const KanbanVersionToggle = ({ onToggle, portalTargetId }) => {
    const [t] = useTranslation();
    const [portalTarget, setPortalTarget] = useState(null);
    const dispatch = useDispatch();
    const workspaceSettings = useSelector(state => state.workspace.settings);
    const currentUser = useSelector(state => state.users.currentUser);
    const ticketsActiveMode = useSelector(state => state.tickets.list.find(category => category?.id === 'ARCHIVED')?.isVisible === false);
    const kanbanVersion = getKanbanVersionWsValue(workspaceSettings.kanban_version, currentUser);

    // Track the portal target, which unmounts while the navbar search is focused
    useEffect(() => {
        if (!portalTargetId) return undefined;
        const updateTarget = () => setPortalTarget(document.getElementById(portalTargetId));
        updateTarget();
        const observer = new MutationObserver(updateTarget);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [portalTargetId]);

    if (kanbanVersion === undefined || !ticketsActiveMode) return null;
    if (portalTargetId && !portalTarget) return null;

    const switchVersion = () => {
        if (onToggle) onToggle(kanbanVersion);
        const hasUserSpecificEntry = workspaceSettings.kanban_version
            .some(entry => entry && Object.keys(entry)[0] === currentUser.email);
        const updatedKanbanVersion = hasUserSpecificEntry
            ? workspaceSettings.kanban_version.map(entry => // Toggle the existing user-specific entry in place
                entry && Object.keys(entry)[0] === currentUser.email
                    ? { [currentUser.email]: kanbanVersion === 'NEW' ? 'OLD' : 'NEW' }
                    : entry
            )
            : [ // No user-specific entry yet — add one to override the "*" default
                ...workspaceSettings.kanban_version,
                { [currentUser.email]: kanbanVersion === 'NEW' ? 'OLD' : 'NEW' }
            ];
        dispatch(updateWorkspaceSetting(
            'kanban_version',
            updatedKanbanVersion,
            Object.prototype.hasOwnProperty.call(workspaceSettings, 'kanban_version'),
            true
        ));
    };

    const toggle = ( // Segmented control between the classic and the beta view
        <div className={classnames('beta-view-toggle', { 'navbar-attached': portalTargetId })}>
            <div className="beta-view-segmented" role="radiogroup">
                {['OLD', 'NEW'].map(version => (
                    <button
                        key={version}
                        type="button"
                        role="radio"
                        aria-checked={kanbanVersion === version}
                        className={classnames('beta-view-option', { selected: kanbanVersion === version })}
                        onClick={() => kanbanVersion !== version && switchVersion()}
                    >
                        {t(version === 'NEW' ? 'tickets:beta_view' : 'tickets:current_view')}
                    </button>
                ))}
            </div>
        </div>
    );

    return portalTargetId ? createPortal(toggle, portalTarget) : toggle;
};

KanbanVersionToggle.propTypes = {
    onToggle: PropTypes.func, // Called with the version before the switch
    portalTargetId: PropTypes.string // Renders the toggle into this DOM element
};

KanbanVersionToggle.defaultProps = {
    onToggle: undefined,
    portalTargetId: undefined
};

export default KanbanVersionToggle;
